import React from 'react';
import { Hammer, Settings2, Wrench, Phone, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';

interface MaintenancePageProps {
  type: 'maintenance' | 'construction' | 'upgrading';
  message: string;
}

const config = {
  maintenance: {
    icon: Settings2,
    title: 'System Maintenance',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    gradient: 'from-blue-600 to-indigo-600',
  },
  construction: {
    icon: Hammer,
    title: 'Under Construction',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    gradient: 'from-orange-500 to-yellow-500',
  },
  upgrading: {
    icon: Wrench,
    title: 'Upgrading System',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    gradient: 'from-emerald-500 to-teal-500',
  }
};

const MaintenancePage: React.FC<MaintenancePageProps> = ({ type = 'maintenance', message }) => {
  const currentConfig = config[type] || config.maintenance;
  const Icon = currentConfig.icon;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 selection:bg-blue-100 selection:text-blue-900 relative overflow-hidden">
      <SEO title={currentConfig.title} description={message} />
      
      {/* Background Decorative Elements */}
      <div className={`absolute top-0 right-0 w-96 h-96 bg-gradient-to-br ${currentConfig.gradient} opacity-[0.03] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2`}></div>
      <div className={`absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr ${currentConfig.gradient} opacity-[0.03] rounded-full blur-3xl translate-y-1/2 -translate-x-1/2`}></div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-xl w-full text-center relative z-10"
      >
        <div className="bg-white rounded-3xl p-10 sm:p-14 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 relative overflow-hidden">
          
          <div className="flex justify-center mb-8">
            <motion.div 
              animate={{ rotate: type === 'maintenance' ? 360 : 0 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className={`w-24 h-24 rounded-2xl ${currentConfig.bg} flex items-center justify-center`}
            >
              <Icon className={`w-12 h-12 ${currentConfig.color}`} />
            </motion.div>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
            {currentConfig.title}
          </h1>
          
          <p className="text-lg text-slate-500 mb-10 leading-relaxed">
            {message || "We are currently making improvements to serve you better. We'll be back shorty!"}
          </p>

          <div className="border-t border-slate-100 pt-8 w-full flex flex-col items-center">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-6">Need urgent assistance?</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
              <a href="mailto:info@makemefiler.com" className="flex items-center justify-center w-full sm:w-auto gap-3 px-6 py-3 rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all text-slate-700 hover:text-blue-700 font-medium group">
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <Mail className="w-4 h-4 text-blue-600" />
                </div>
                info@makemefiler.com
              </a>
              <a href="tel:03225511770" className="flex items-center justify-center w-full sm:w-auto gap-3 px-6 py-3 rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all text-slate-700 hover:text-emerald-700 font-medium group">
                <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                  <Phone className="w-4 h-4 text-emerald-600" />
                </div>
                03225511770
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-slate-400 font-medium">
          Make Me Filer &copy; {new Date().getFullYear()}
        </div>
      </motion.div>
    </div>
  );
};

export default MaintenancePage;
